import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Button,
  DatePicker,
  EmptyState,
  Input,
  ReportTable,
  type ReportTablePaginationState,
  type ReportTableSort,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { ArrowLeft, Download } from 'lucide-react';
import { ReportMultiSelectFilter } from '../components/reporting/ReportMultiSelectFilter';
import { ReportPopulationControls } from '../components/reporting/ReportPopulationControls';
import { buildReportPopulationQuery } from '../components/reporting/reportPopulation';
import { PermissionSlug } from '../constants/featureFlags';
import {
  exportScheduleReportReportsScheduleExportGet,
  getScheduleReportOptionsReportsScheduleOptionsGet,
  getScheduleReportReportsScheduleGet,
} from '../services/api/sdk.gen';
import type {
  MissedVisitFilter,
  ScheduleReportResult,
  ScheduleReportRow,
  ScheduleSortBy,
  SortOrder as SortDirection,
} from '../services/api/types.gen';
import { downloadResponseAsFile } from '../utils/downloadFile';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessageAsync,
} from '../utils/apiErrorDetail';
import { useShallow } from 'zustand/react/shallow';
import ReportsPermissionAlert from './ReportsPermissionAlert';
import { useReportsBackHref } from '../reporting/backHref';
import { useStore } from '../store';
import {
  formatDateToISO,
  getBusinessToday,
  parseISOToLocalDate,
} from '../utils/dateUtils';
import { ReportLink } from '../components/reporting/ReportLink';
import {
  formatReportEmptyValue,
  readChoiceParam,
  readDelimitedParam,
  readOptionalChoiceParam,
  readPositiveIntegerParam,
  REPORT_PAGE_SIZE_OPTIONS,
} from '../utils/reportDisplay';
import { patientChartPath, patientVisitPath } from '../utils/navigation';

const ALL_CLINICS_VALUE = '__all_clinics__';
const VALID_MISSED_FILTERS: MissedVisitFilter[] = [
  'all',
  'missed',
  'not_missed',
];
const VALID_SORT_BY: ScheduleSortBy[] = [
  'clinic_name',
  'patient_last_name',
  'patient_first_name',
  'mrn',
  'scheduled_date',
  'visit_date',
  'visit_type',
  'status',
  'assigned_clinician',
  'discipline',
  'payer',
];
const VALID_SORT_DIRECTIONS: SortDirection[] = ['asc', 'desc'];

type ReportOption = { id: string; label: string };
type ScheduleOptionsResponse = {
  clinics: ReportOption[];
  clinicians: ReportOption[];
  statuses: ReportOption[];
  visit_types: ReportOption[];
  disciplines: ReportOption[];
};

const columns = [
  {
    id: 'clinic_name',
    header: 'Clinic',
    accessor: (row: ScheduleReportRow) =>
      formatReportEmptyValue(row.clinic_name),
    enableSorting: true,
    minWidth: 160,
  },
  {
    id: 'patient_last_name',
    header: 'Patient Last',
    accessor: 'patient_last_name' as const,
    render: (_value: unknown, row: ScheduleReportRow) => (
      <ReportLink to={patientChartPath(row.patient_id)}>
        {row.patient_last_name}
      </ReportLink>
    ),
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'patient_first_name',
    header: 'Patient First',
    accessor: 'patient_first_name' as const,
    render: (_value: unknown, row: ScheduleReportRow) => (
      <ReportLink to={patientChartPath(row.patient_id)}>
        {row.patient_first_name}
      </ReportLink>
    ),
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'mrn',
    header: 'MRN',
    accessor: (row: ScheduleReportRow) => formatReportEmptyValue(row.mrn),
    enableSorting: true,
    minWidth: 120,
  },
  {
    id: 'phone',
    header: 'Phone',
    accessor: (row: ScheduleReportRow) => formatReportEmptyValue(row.phone),
    minWidth: 140,
  },
  {
    id: 'payer',
    header: 'Episode Payer',
    accessor: (row: ScheduleReportRow) => formatReportEmptyValue(row.payer),
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'scheduled_date',
    header: 'Scheduled Date',
    accessor: 'scheduled_date' as const,
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'visit_date',
    header: 'Visit Date',
    accessor: (row: ScheduleReportRow) =>
      formatReportEmptyValue(row.visit_date),
    enableSorting: true,
    minWidth: 130,
  },
  {
    id: 'visit_type',
    header: 'Visit Type',
    accessor: (row: ScheduleReportRow) =>
      formatReportEmptyValue(row.visit_type_label),
    render: (_value: unknown, row: ScheduleReportRow) => {
      const label = formatReportEmptyValue(row.visit_type_label);
      if (!row.visit_id) return label;
      return (
        <ReportLink to={patientVisitPath(row.patient_id, row.visit_id)}>
          {label}
        </ReportLink>
      );
    },
    enableSorting: true,
    minWidth: 220,
  },
  {
    id: 'discipline',
    header: 'Discipline',
    accessor: (row: ScheduleReportRow) =>
      formatReportEmptyValue(row.discipline),
    enableSorting: true,
    minWidth: 120,
  },
  {
    id: 'status',
    header: 'Status',
    accessor: (row: ScheduleReportRow) => formatReportEmptyValue(row.status),
    enableSorting: true,
    minWidth: 170,
  },
  {
    id: 'missed',
    header: 'Missed',
    accessor: (row: ScheduleReportRow) => (row.missed ? 'Yes' : 'No'),
    minWidth: 100,
  },
  {
    id: 'assigned_clinician',
    header: 'Assigned Clinician',
    accessor: (row: ScheduleReportRow) =>
      formatReportEmptyValue(row.assigned_clinician),
    enableSorting: true,
    minWidth: 190,
  },
  {
    id: 'clinician_credentials',
    header: 'Credentials',
    accessor: (row: ScheduleReportRow) =>
      formatReportEmptyValue(row.clinician_credentials),
    minWidth: 140,
  },
  {
    id: 'scheduling_notes',
    header: 'Scheduling Notes',
    accessor: (row: ScheduleReportRow) =>
      formatReportEmptyValue(row.scheduling_notes),
    minWidth: 260,
  },
  {
    id: 'time_in',
    header: 'Time In',
    accessor: (row: ScheduleReportRow) => formatReportEmptyValue(row.time_in),
    minWidth: 110,
  },
  {
    id: 'time_out',
    header: 'Time Out',
    accessor: (row: ScheduleReportRow) => formatReportEmptyValue(row.time_out),
    minWidth: 110,
  },
  {
    id: 'duration_minutes',
    header: 'Duration',
    accessor: (row: ScheduleReportRow) =>
      formatReportEmptyValue(row.duration_minutes),
    align: 'right' as const,
    minWidth: 110,
  },
];

function getDefaultDateRange(): { dateFrom: string; dateTo: string } {
  // Match the Kinnser-style starting view: today plus the next seven days.
  const today = getBusinessToday();
  const toDate = parseISOToLocalDate(today);
  if (!toDate) return { dateFrom: today, dateTo: today };
  toDate.setDate(toDate.getDate() + 7);
  return { dateFrom: today, dateTo: formatDateToISO(toDate) };
}

type ScheduleQueryFilters = {
  dateFrom: string;
  dateTo: string;
  clinicId: string;
  clinicianId: string;
  statuses: string;
  visitTypes: string;
  disciplines: string;
  missed: MissedVisitFilter;
  q: string;
  includeTestRows: boolean;
  includeInactiveEpisodes: boolean;
  sortBy?: ScheduleSortBy;
  sortDirection?: SortDirection;
};

function buildScheduleReportQuery(filters: ScheduleQueryFilters) {
  return {
    date_from: filters.dateFrom,
    date_to: filters.dateTo,
    clinic_id: filters.clinicId || null,
    clinician_ids: readDelimitedParam(filters.clinicianId),
    statuses: filters.statuses || null,
    visit_types: filters.visitTypes || null,
    disciplines: filters.disciplines || null,
    missed: filters.missed,
    q: filters.q || null,
    ...buildReportPopulationQuery(filters),
    sort_by: filters.sortBy ?? null,
    sort_direction: filters.sortDirection ?? null,
  };
}

const ScheduleReport = () => {
  const { hasPermission } = useStore(
    useShallow(state => ({ hasPermission: state.hasPermission }))
  );
  const reportsBackHref = useReportsBackHref();
  const [searchParams, setSearchParams] = useSearchParams();
  const [options, setOptions] = useState<ScheduleOptionsResponse>({
    clinics: [],
    clinicians: [],
    statuses: [],
    visit_types: [],
    disciplines: [],
  });
  const [report, setReport] = useState<ScheduleReportResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasUrlDateRange = searchParams.has('from') || searchParams.has('to');
  const defaultDateRange = useMemo(() => getDefaultDateRange(), []);
  const dateFrom =
    searchParams.get('from') ??
    (hasUrlDateRange ? '' : defaultDateRange.dateFrom);
  const dateTo =
    searchParams.get('to') ?? (hasUrlDateRange ? '' : defaultDateRange.dateTo);
  const hasIncompleteRange = Boolean(dateFrom) !== Boolean(dateTo);
  const clinicId = searchParams.get('clinic') ?? '';
  const clinicianId = searchParams.get('clinician') ?? '';
  const statuses = searchParams.get('status') ?? '';
  const visitTypes = searchParams.get('visit_type') ?? '';
  const disciplines = searchParams.get('discipline') ?? '';
  const missed = readChoiceParam(
    searchParams.get('missed'),
    VALID_MISSED_FILTERS,
    'all'
  );
  const q = searchParams.get('q') ?? '';
  const includeTestRows = searchParams.get('include_test_rows') === 'true';
  const includeInactiveEpisodes =
    searchParams.get('include_inactive_episodes') === 'true';
  const page = readPositiveIntegerParam(searchParams.get('page'));
  const pageSize = readChoiceParam(
    searchParams.get('size'),
    REPORT_PAGE_SIZE_OPTIONS,
    25
  );
  const sortBy = readOptionalChoiceParam(
    searchParams.get('sort'),
    VALID_SORT_BY
  );
  const sortDirection = readOptionalChoiceParam(
    searchParams.get('dir'),
    VALID_SORT_DIRECTIONS
  );

  const queryFilters = useMemo(
    () => ({
      dateFrom,
      dateTo,
      clinicId,
      clinicianId,
      statuses,
      visitTypes,
      disciplines,
      missed,
      q,
      includeTestRows,
      includeInactiveEpisodes,
      sortBy,
      sortDirection,
    }),
    [
      dateFrom,
      dateTo,
      clinicId,
      clinicianId,
      statuses,
      visitTypes,
      disciplines,
      missed,
      q,
      includeTestRows,
      includeInactiveEpisodes,
      sortBy,
      sortDirection,
    ]
  );

  const updateParams = useCallback(
    (updates: Record<string, string | null>, resetPage = true) => {
      setSearchParams(current => {
        const next = new URLSearchParams(current);
        Object.entries(updates).forEach(([key, value]) => {
          if (value) next.set(key, value);
          else next.delete(key);
        });
        if (resetPage) next.set('page', '1');
        return next;
      });
    },
    [setSearchParams]
  );

  const updateDateParam = useCallback(
    (field: 'from' | 'to', value: string | null) => {
      const updates: Record<string, string | null> = { [field]: value };
      if (!hasUrlDateRange) {
        // The default dates are intentionally implicit. Once the user edits one
        // side, write the other side too so the DatePicker change does not turn
        // the default report into a one-sided range by accident.
        updates[field === 'from' ? 'to' : 'from'] =
          field === 'from'
            ? defaultDateRange.dateTo
            : defaultDateRange.dateFrom;
      }
      updateParams(updates);
    },
    [
      defaultDateRange.dateFrom,
      defaultDateRange.dateTo,
      hasUrlDateRange,
      updateParams,
    ]
  );

  useEffect(() => {
    if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)) return;
    let cancelled = false;
    getScheduleReportOptionsReportsScheduleOptionsGet({
      query: {
        date_from: dateFrom || null,
        date_to: dateTo || null,
        ...buildReportPopulationQuery({
          includeTestRows,
          includeInactiveEpisodes,
        }),
      },
    })
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          setError('Unable to load schedule report. Please try again.');
          return;
        }
        setOptions(response.data as ScheduleOptionsResponse);
      })
      .catch(error => {
        console.error('Failed to load schedule report options', error);
        if (!cancelled)
          setError('Unable to load schedule report. Please try again.');
      });
    return () => {
      cancelled = true;
    };
  }, [
    hasPermission,
    dateFrom,
    dateTo,
    includeInactiveEpisodes,
    includeTestRows,
  ]);

  useEffect(() => {
    if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)) return;
    if (!dateFrom && !dateTo) {
      setReport(null);
      setIsLoading(false);
      setError(null);
      return;
    }
    if (hasIncompleteRange) {
      setReport(null);
      setIsLoading(false);
      setError('Select both From and To dates.');
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    getScheduleReportReportsScheduleGet({
      query: {
        ...buildScheduleReportQuery(queryFilters),
        page,
        page_size: pageSize,
      },
    })
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          setError(
            getBusinessErrorMessage({
              response: {
                status: response.response?.status,
                data: response.error,
              },
            }) ?? 'Unable to load schedule report. Please try again.'
          );
          setReport(null);
          return;
        }
        setReport(response.data);
      })
      .catch(error => {
        console.error('Failed to load schedule report', error);
        if (!cancelled) {
          setError(
            getBusinessErrorMessage(error) ??
              'Unable to load schedule report. Please try again.'
          );
          setReport(null);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    hasPermission,
    dateFrom,
    dateTo,
    hasIncompleteRange,
    queryFilters,
    page,
    pageSize,
  ]);

  const pagination: ReportTablePaginationState = useMemo(
    () => ({ page, pageSize }),
    [page, pageSize]
  );

  const sorting: ReportTableSort | null = sortBy
    ? { id: sortBy, direction: sortDirection ?? 'asc' }
    : null;

  if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS))
    return <ReportsPermissionAlert />;

  const emptyState = error ? (
    <EmptyState role='alert' title={error} description='Please try again.' />
  ) : !dateFrom || !dateTo ? (
    <EmptyState
      title='Select a date range'
      description='Choose From and To dates to load scheduled visits.'
    />
  ) : (
    <EmptyState
      title='No matching scheduled visits'
      description='Adjust filters and try again.'
    />
  );

  const download = async () => {
    if (!dateFrom || !dateTo || hasIncompleteRange) {
      setError('Select both From and To dates.');
      return;
    }
    try {
      const response = await exportScheduleReportReportsScheduleExportGet({
        query: buildScheduleReportQuery(queryFilters),
        responseType: 'blob' as const,
      } as never);
      if (response.error || !response.data) {
        setError(
          await getUserFacingApiMessageAsync(
            {
              response: {
                status: response.response?.status,
                data: response.response?.data ?? response.error,
              },
            },
            'Unable to download schedule report. Please try again.'
          )
        );
        return;
      }
      downloadResponseAsFile(response as never, 'schedule-report.xlsx');
    } catch (error) {
      console.error('Failed to download schedule report', error);
      setError(
        await getUserFacingApiMessageAsync(
          error,
          'Unable to download schedule report. Please try again.'
        )
      );
    }
  };

  return (
    <div className='space-y-6'>
      <Link
        to={reportsBackHref}
        className='inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground'
      >
        <ArrowLeft className='mr-1 h-4 w-4' /> Reports
      </Link>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <h1 className='text-2xl font-bold'>Schedule Report</h1>
          <p className='text-sm text-muted-foreground'>
            Review scheduled visits by date, clinician, and status.
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => void download()}
            disabled={isLoading || !dateFrom || !dateTo || hasIncompleteRange}
          >
            <Download className='h-4 w-4' /> Export
          </Button>
          <ReportPopulationControls
            includeTestRows={includeTestRows}
            includeInactiveEpisodes={includeInactiveEpisodes}
            onIncludeTestRowsChange={checked =>
              updateParams({ include_test_rows: checked ? 'true' : null })
            }
            onIncludeInactiveEpisodesChange={checked =>
              updateParams({
                include_inactive_episodes: checked ? 'true' : null,
              })
            }
          />
        </div>
      </div>

      {error ? (
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800'>{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className='space-y-4 rounded-lg border bg-card p-4'>
        <div className='grid gap-3 md:grid-cols-4'>
          <div className='space-y-1 text-sm font-medium'>
            <label htmlFor='schedule-date-from'>From</label>
            <DatePicker
              id='schedule-date-from'
              date={parseISOToLocalDate(dateFrom)}
              datePickerSize='default'
              onDateChange={date =>
                updateDateParam('from', date ? formatDateToISO(date) : null)
              }
              showClearButton
            />
          </div>
          <div className='space-y-1 text-sm font-medium'>
            <label htmlFor='schedule-date-to'>To</label>
            <DatePicker
              id='schedule-date-to'
              date={parseISOToLocalDate(dateTo)}
              datePickerSize='default'
              onDateChange={date =>
                updateDateParam('to', date ? formatDateToISO(date) : null)
              }
              showClearButton
            />
          </div>
          <label
            className='space-y-1 text-sm font-medium'
            htmlFor='schedule-clinic-filter'
          >
            Clinic
            <Select
              value={clinicId || ALL_CLINICS_VALUE}
              onValueChange={value =>
                updateParams({
                  clinic: value === ALL_CLINICS_VALUE ? null : value,
                })
              }
            >
              <SelectTrigger id='schedule-clinic-filter' selectSize='default'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_CLINICS_VALUE}>All clinics</SelectItem>
                {options.clinics.map(option => (
                  <SelectItem key={option.id} value={option.id}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <ReportMultiSelectFilter
            label='Assigned clinician'
            value={clinicianId}
            options={options.clinicians}
            allLabel='All clinicians'
            deferApply
            onChange={value => updateParams({ clinician: value || null })}
          />
          <label
            className='space-y-1 text-sm font-medium'
            htmlFor='schedule-missed-filter'
          >
            Missed visit
            <Select
              value={missed}
              onValueChange={value => updateParams({ missed: value || null })}
            >
              <SelectTrigger id='schedule-missed-filter' selectSize='default'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All</SelectItem>
                <SelectItem value='missed'>Missed only</SelectItem>
                <SelectItem value='not_missed'>Not missed</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <label className='space-y-1 text-sm font-medium md:col-span-2'>
            Search
            <Input
              value={q}
              onChange={event =>
                updateParams({ q: event.target.value || null })
              }
              placeholder='Patient, MRN, phone, clinician, or payer'
            />
          </label>
        </div>
        <div className='grid gap-3 md:grid-cols-3'>
          <ReportMultiSelectFilter
            label='Status'
            value={statuses}
            options={options.statuses}
            allLabel='All statuses'
            onChange={value => updateParams({ status: value || null })}
          />
          <ReportMultiSelectFilter
            label='Visit type'
            value={visitTypes}
            options={options.visit_types}
            allLabel='All visit types'
            onChange={value => updateParams({ visit_type: value || null })}
          />
          <ReportMultiSelectFilter
            label='Discipline'
            value={disciplines}
            options={options.disciplines}
            allLabel='All disciplines'
            onChange={value => updateParams({ discipline: value || null })}
          />
        </div>
      </div>

      <ReportTable
        data={report?.items ?? []}
        columns={columns}
        getRowId={row => row.row_id}
        caption='Schedule report results'
        sorting={sorting}
        onSortingChange={next => {
          updateParams(
            next
              ? { sort: next.id, dir: next.direction }
              : { sort: null, dir: null }
          );
        }}
        isLoading={isLoading}
        emptyState={emptyState}
        pagination={{
          ...pagination,
          total: report?.total ?? 0,
          totalPages: Math.ceil((report?.total ?? 0) / pageSize),
        }}
        onPaginationChange={next => {
          setSearchParams(current => {
            const params = new URLSearchParams(current);
            params.set('page', String(next.page));
            params.set('size', String(next.pageSize));
            return params;
          });
        }}
      />
    </div>
  );
};

export default ScheduleReport;
